import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { MailCheck, RefreshCw, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { authErrorMessage } from '../../lib/authErrors';
import { Button } from '../ui';
import { AuthAlert } from './AuthShell';

/**
 * Wraps the authenticated app. If the signed-in user's email is not verified,
 * the entire app surface is replaced with a "verify your email" screen that can
 * resend the verification email and re-check status.
 */
export function RequireVerifiedEmail() {
  const { user, isVerified, sendEmailVerification, reloadUser, signOut } = useAuth();
  const [status, setStatus] = useState<{ tone: 'error' | 'success'; msg: string } | null>(null);
  const [busy, setBusy] = useState<'resend' | 'check' | null>(null);

  if (isVerified) return <Outlet />;

  const resend = async () => {
    setBusy('resend');
    setStatus(null);
    try {
      await sendEmailVerification();
      setStatus({ tone: 'success', msg: 'Verification email sent. Check your inbox (and spam).' });
    } catch (err) {
      setStatus({ tone: 'error', msg: authErrorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  const check = async () => {
    setBusy('check');
    setStatus(null);
    try {
      await reloadUser();
      // If still unverified after reload, nudge the user.
      setStatus({ tone: 'error', msg: 'Still not verified yet — click the link in your email first.' });
    } catch (err) {
      setStatus({ tone: 'error', msg: authErrorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-10 text-center shadow-sm">
        <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-blue-600">
          <MailCheck className="h-8 w-8" />
        </span>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Verify your email</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          We sent a verification link to <span className="font-semibold text-gray-700 dark:text-gray-200">{user?.email}</span>. Confirm
          your address to unlock Teskedo.
        </p>

        {status && (
          <div className="mt-5 text-left">
            <AuthAlert tone={status.tone}>{status.msg}</AuthAlert>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3">
          <Button onClick={check} loading={busy === 'check'} leftIcon={<RefreshCw className="h-4 w-4" />}>
            I've verified — continue
          </Button>
          <Button variant="outline" onClick={resend} loading={busy === 'resend'}>
            Resend verification email
          </Button>
          <Button variant="ghost" onClick={() => signOut()} leftIcon={<LogOut className="h-4 w-4" />}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
